var _excluded = ["options", "searchBarProps", "value", "size", "emptyState", "data-testid", "onClick", "onChange", "onClear", "optionRenderer"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { isEqual, pickBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTestId } from '../../../utils';
export var useSelect = _ref => {
  var _searchBarProps$optio;

  var {
    options: optionsList,
    searchBarProps,
    value,
    size,
    emptyState,
    'data-testid': dataTestId = 'select',
    onClick: _onClick,
    onChange,
    onClear: _onClear,
    optionRenderer
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var isUncontrolled = value && !onChange;
  useEffect(() => {
    if (isUncontrolled) {
      // eslint-disable-next-line no-console -- We need to log a warning if `value` is provided without `onChange`.
      console.error('Warning: You provided a `value` prop to a form field without an `onChange` handler. This will render a read-only field. Please set either `onChange` or `isReadOnly`.');
    }
  }, [isUncontrolled]);
  var [options, setOptions] = useState(optionsList); // Makes sure we get the latest options list.

  useEffect(() => {
    setOptions(optionsList);
  }, [optionsList]);
  var defaultValue = useMemo(() => {
    var _options$find;

    return (_options$find = options.find(option => isEqual(option.value, value))) !== null && _options$find !== void 0 ? _options$find : null;
  }, [options, value]);
  var [selectedOption, setSelectedOption] = useState(defaultValue);
  useEffect(() => {
    if (isUncontrolled) return;
    setSelectedOption(defaultValue);
  }, [isUncontrolled, defaultValue]);

  var handleChange = newSelectedOption => {
    var _newSelectedOption$va;

    var isSameOption = isEqual(newSelectedOption, selectedOption);
    if (isUncontrolled || isSameOption) return;
    setSelectedOption(newSelectedOption);
    onChange === null || onChange === void 0 ? void 0 : onChange({
      target: {
        value: (_newSelectedOption$va = newSelectedOption === null || newSelectedOption === void 0 ? void 0 : newSelectedOption.value) !== null && _newSelectedOption$va !== void 0 ? _newSelectedOption$va : null
      }
    });
  };

  var [isOpen, setIsOpen] = useState(false);
  var getTestId = useTestId(dataTestId);
  var [activeOptionId, setActiveOptionId] = useState('');

  var getOptionProps = _ref2 => {
    var {
      option,
      index
    } = _ref2;
    return _objectSpread(_objectSpread({}, getTestId('option', option.testId || "".concat(index))), {}, {
      option,
      optionRenderer,
      onClick: () => {
        handleChange(option);
        setIsOpen(false);
      },
      isSelected: isEqual(option, selectedOption),
      id: "select-option-".concat(index),
      setActiveOptionId
    });
  };

  useEffect(() => {
    var _searchBarProps$onCha;

    if (isOpen) return;
    searchBarProps === null || searchBarProps === void 0 ? void 0 : (_searchBarProps$onCha = searchBarProps.onChange) === null || _searchBarProps$onCha === void 0 ? void 0 : _searchBarProps$onCha.call(searchBarProps, {
      target: {
        value: ''
      }
    }); // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this effect when the menu is closed.
  }, [isOpen]);
  return {
    options: (_searchBarProps$optio = searchBarProps === null || searchBarProps === void 0 ? void 0 : searchBarProps.options) !== null && _searchBarProps$optio !== void 0 ? _searchBarProps$optio : options,
    triggerProps: _objectSpread(_objectSpread({
      onClick: event => {
        setIsOpen(!isOpen);
        _onClick === null || _onClick === void 0 ? void 0 : _onClick(event);
      },
      onKeyDown: event => {
        // We need to manually handle the space key because our trigger is an input.
        if (event.code === 'Space') setIsOpen(!isOpen);
      },
      onClear: () => {
        handleChange(null);
        _onClear === null || _onClear === void 0 ? void 0 : _onClear();
      },
      isOpen,
      setIsOpen,
      size,
      selectedOption,
      'aria-activedescendant': isOpen ? activeOptionId : ''
    }, getTestId()), otherProps),
    menuProps: _objectSpread({
      isOpen,
      onOpenChange: setIsOpen,
      disableMenuShift: true,
      // We disable the typeahead feature if `searchBarProps` is provided because it handles the filtering.
      disableTypeahead: Boolean(searchBarProps),
      role: 'listbox'
    }, getTestId('menu')),
    // We need to remove `options` from `searchBarProps` because it's not a valid prop for `SearchBar`.
    searchBarProps: searchBarProps && pickBy(searchBarProps, (_value, key) => key !== 'options'),
    getOptionProps,
    emptyStateProps: _objectSpread({
      emptyState
    }, getTestId('empty-state'))
  };
};
//# sourceMappingURL=useSelectNew.js.map